/**
 * DashboardHeader — dashboard top bar (IA v3, Phase 5).
 *
 * Renders as an island component (`studio.dashboard.header`) registered in
 * `studio-dashboard-shell.tsx`. Layout:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ [Breadcrumb] │ [Search Cmd+K]  ·  [chat][theme]                   │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Breadcrumb (left):
 *   - Chain and labels both come from `studio-route-registry.ts` (each
 *     entry's `parent` link + `studioRouteLabel`), so the breadcrumb, the
 *     nav rail, and the command palette always name a screen identically.
 *   - Always shows `Home` segment linking to `/studio`.
 *   - Operations routes (`commerce` / `products` / `orders` / `revenue` /
 *     `members` / `billing` / `analytics`) carry `parent: 'commerce'`, which
 *     renders `Home / Operations / <Leaf>`. These routes are still available for
 *     existing links, but they are no longer primary rail destinations.
 *   - Top-level dashboard routes carry `parent: 'home'` and show
 *     `Home / <Label>`; the root `home` route has no parent and shows the
 *     single word "Home" (non-clickable) as a header.
 *   - Clickable segments use the shared pattern from `top-bar.tsx` —
 *     `setStudioRoute + pushState + PopStateEvent('popstate')` so the
 *     App router picks up the change.
 *
 * Responsive contract (the `BREADCRUMB_ANCESTOR_*` classes below):
 *   Below `sm` (<640px) the header row is fully subscribed by fixed-width
 *   chrome — nav toggle (40px) + wordmark (94px) + action cluster (132px)
 *   + gaps left the breadcrumb only ~54px of a 358px content box, so every
 *   crumb collapsed to a 1–22px sliver ("H", "L.") with no ellipsis and no
 *   tooltip. Because the breadcrumb is the ONLY flex child that may shrink
 *   (siblings are `shrink-0`, and the `flex-1` spacer has `basis:0` so it
 *   cannot yield), it absorbed 100% of the deficit.
 *
 *   Fix — below `sm` the header degrades to the standard mobile app-bar
 *   shape `[≡] [current screen title] … [actions]`:
 *     - ancestor crumbs (and their trailing `/`) are CSS-hidden, so the
 *       leaf inherits the whole region instead of splitting it;
 *     - the redundant wordmark yields too (the same lockup already renders
 *       inside the drawer that `≡` opens — `creator-nav-brand`), which is
 *       what buys the leaf enough room to render in full at 375px;
 *     - the leaf drops its horizontal padding so it aligns as a title.
 *   Ancestors stay in the DOM (hidden, not unmounted) so breadcrumb text
 *   assertions and the drawer's own up-navigation stay intact. At `sm` and
 *   above the full clickable chain renders exactly as before.
 *
 *   Truncation is now the graceful fallback rather than the default: every
 *   crumb label carries a `title` so an unavoidably long name (a locale
 *   with a wider translation) degrades to `…` + tooltip, never to a sliver.
 *
 * Search + chat controls (right):
 *   - Cmd+K search palette trigger is kept (finding things).
 *   - Natural-language generation happens only in the chat drawer composer.
 *     The header exposes a chat toggle, not a second prompt field.
 *
 * CommandPalette coordination (unchanged from Phase 2/3):
 *   The search input opens the shell's CommandPalette by dispatching a
 *   `dashboard-open-command-palette` CustomEvent on window. The shell
 *   wrapper listens for it and flips its local `commandPaletteOpen`
 *   state. Same pattern as `analytics-navigate`.
 */
import { useCallback } from 'react'
import { MessageSquare, Sun, Moon } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import type { StudioRoute } from '@/store/types'
import { useTheme } from '@/hooks/use-theme'
import { pushStudioRoute } from '@/lib/studio-navigation'
import {
  studioRouteAncestry,
  studioRouteLabel,
  studioRouteOwnsConversation,
} from '@/lib/studio-route-registry'
import { useUIBreakpoint } from '@/hooks/use-ui-breakpoint'
import { CREATOR_NAV_OPEN_EVENT } from '@/shell/creator-nav-events'
import { CogoWordmark } from '@/components/cogo-logo'
import { StudioLanguageSwitcher, useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

// ─── Breadcrumb ─────────────────────────────────────────────────────────

type CrumbRoute = StudioRoute | 'home'

interface Crumb {
  label: string
  route: CrumbRoute | null // null = non-clickable leaf
}

/**
 * The crumb chain for a route, derived from the route registry.
 *
 * The chain shape (`Home` / `Home / <Leaf>` / `Home / Operations / <Leaf>`) comes
 * from each entry's `parent` link, and every name from `studioRouteLabel`.
 * Both used to live here as three hand-maintained maps that were a fourth
 * independent copy of the route names, and had drifted from the screens they
 * label: `map` read `Structure` under an `App Map` heading,
 * `design-system` read `Style` under a `Design System` heading. Deriving
 * them makes the breadcrumb, the nav rail, the command palette, and the
 * registry structurally incapable of disagreeing.
 */
function breadcrumbFor(route: StudioRoute, strings: StudioDictionary): Crumb[] {
  const ancestry = studioRouteAncestry(route)
  return ancestry.map((crumbRoute, index) => ({
    label: studioRouteLabel(crumbRoute, strings),
    // Only ancestors navigate; the leaf is the page you are already on.
    route: index === ancestry.length - 1 ? null : crumbRoute,
  }))
}

/**
 * Ancestor crumbs collapse below `sm` so the leaf (the answer to "where am
 * I?") owns the whole region. Kept in the DOM — `hidden` only — so the
 * rendered breadcrumb text stays assertable and nothing else has to know
 * about the breakpoint.
 */
const BREADCRUMB_ANCESTOR_ITEM_CLASS = 'hidden min-w-0 items-center sm:flex'
const BREADCRUMB_LEAF_ITEM_CLASS = 'flex min-w-0 items-center'

function BreadcrumbNav() {
  const { strings } = useStudioLocale()
  const studioRoute = useStudioStore((s) => s.studioRoute)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)

  const navigate = useCallback(
    (target: CrumbRoute) => {
      setStudioRoute(target as StudioRoute)
      pushStudioRoute(target)
    },
    [setStudioRoute],
  )

  const crumbs = breadcrumbFor(studioRoute as StudioRoute, strings)

  return (
    <nav
      aria-label={strings.chrome.breadcrumb}
      data-testid="dashboard-header-breadcrumb"
      className="flex min-w-0 items-center"
    >
      <ol className="flex min-w-0 items-center gap-0 overflow-hidden">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1
          return (
            <li
              key={`${crumb.label}-${idx}`}
              className={
                isLast
                  ? BREADCRUMB_LEAF_ITEM_CLASS
                  : BREADCRUMB_ANCESTOR_ITEM_CLASS
              }
            >
              {crumb.route ? (
                <button
                  type="button"
                  onClick={() => navigate(crumb.route!)}
                  data-testid={`dashboard-header-breadcrumb-${crumb.route}`}
                  className="inline-flex h-10 min-w-0 items-center rounded-md px-2 text-sm text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                >
                  <span className="truncate" title={crumb.label}>
                    {crumb.label}
                  </span>
                </button>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  data-testid="dashboard-header-breadcrumb-current"
                  // `px-0` below `sm`: as the lone label it reads as the app-bar
                  // title, and the 16px it gives back is real estate the longest
                  // label ("Workspace Settings") actually needs at 375px.
                  className="inline-flex h-10 min-w-0 items-center px-0 text-sm font-medium text-foreground sm:px-2"
                >
                  <span className="truncate" title={crumb.label}>
                    {crumb.label}
                  </span>
                </span>
              )}
              {!isLast && (
                <span
                  aria-hidden="true"
                  className="text-sm text-muted-foreground/50 mx-0.5 select-none"
                >
                  /
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ─── Right-side buttons ─────────────────────────────────────────────────

function ChatToggleButton() {
  const { strings } = useStudioLocale()
  const chatSidebarOpen = useStudioStore((s) => s.chatSidebarOpen)
  const toggleChatSidebar = useStudioStore((s) => s.toggleChatSidebar)
  const studioRoute = useStudioStore((s) => s.studioRoute)
  // On a screen that hosts its own conversation there is no rail to toggle,
  // so the control is not rendered rather than left on screen answering a
  // press with nothing. The alternative — keeping it and explaining itself —
  // was rejected because the explanation would have to be given on the two
  // screens where the request box is the largest thing on the page, i.e.
  // where the answer is already visible.
  if (studioRouteOwnsConversation(studioRoute)) return null
  return (
    <button
      type="button"
      onClick={toggleChatSidebar}
      aria-label={
        chatSidebarOpen ? strings.chrome.closeChat : strings.chrome.openChat
      }
      aria-pressed={chatSidebarOpen}
      data-testid="dashboard-header-chat-toggle"
      className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
    >
      <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
    </button>
  )
}

/** Dispatched by the search input. Shell wrapper's `useEffect` listener flips
 *  `commandPaletteOpen` state. Exported so tests can fire it directly. */
export const COMMAND_PALETTE_OPEN_EVENT = 'dashboard-open-command-palette'

export function DashboardHeader() {
  const { strings } = useStudioLocale()
  const studioRoute = useStudioStore((s) => s.studioRoute)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const openCommandPalette = useCallback(() => {
    window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT))
  }, [])

  const { resolvedTheme, setTheme } = useTheme()
  const { bp } = useUIBreakpoint()
  const isMobileShell =
    bp === 'md' && typeof window !== 'undefined' && window.innerWidth < 1024

  const toggleNavigation = useCallback(() => {
    window.dispatchEvent(new Event(CREATOR_NAV_OPEN_EVENT))
  }, [])

  return (
    <header
      data-testid="dashboard-header"
      className="flex min-w-0 items-center gap-2 border-b border-border/40 px-4 py-3 lg:gap-3 lg:px-6"
    >
      {isMobileShell && (
        <button
          type="button"
          onClick={toggleNavigation}
          aria-label={strings.chrome.openNavigation}
          data-testid="dashboard-header-mobile-nav-toggle"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          <svg
            className="w-4.5 h-4.5 stroke-current"
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      )}
      {/* Brand lockup renders only where it costs the breadcrumb nothing —
          the `sm`–`md` band (640–767px).
            - below `sm`: the 94px lockup is the single biggest claim on a
              358px row, and it is redundant with the drawer this header's `≡`
              opens (`creator-nav-brand`), so it yields to the screen title;
            - from `md`: the 224px search field claims the row instead, which
              left the longest leaf ("Workspace Settings") 2px short of its
              128px even before ancestors were collapsed — the lockup yields
              again rather than trade a brand mark for an ellipsis;
            - from `lg`: the sidebar owns the brand (pre-existing `lg:hidden`).
          Sidebar/drawer keep the brand present at every width. */}
      <span
        aria-hidden="true"
        data-testid="dashboard-header-wordmark"
        className="hidden h-10 w-[100px] shrink-0 items-center sm:flex md:hidden"
      >
        <CogoWordmark size={36} />
      </span>
      <BreadcrumbNav />
      <div className="flex-1" />
      <div className="relative w-56 lg:w-64 hidden md:block">
        <input
          type="text"
          placeholder={strings.chrome.search}
          readOnly
          onClick={openCommandPalette}
          data-testid="dashboard-header-search"
          className="h-10 w-full cursor-pointer rounded-lg border border-border/50 bg-muted/30 px-3 pr-12 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
          &#8984;K
        </span>
      </div>
      <div className="flex items-center gap-1">
        <ChatToggleButton />
        {isMobileShell ? <StudioLanguageSwitcher compact /> : null}
        <button
          type="button"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          aria-label={
            resolvedTheme === 'dark'
              ? strings.chrome.lightMode
              : strings.chrome.darkMode
          }
          className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          {resolvedTheme === 'dark' ? (
            <Sun className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          ) : (
            <Moon
              className="w-4 h-4 text-muted-foreground"
              strokeWidth={1.75}
            />
          )}
        </button>
      </div>
    </header>
  )
}
